import { useContext, useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import axios, { AxiosError } from "axios";
import useSWR from "swr";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { Form } from "../../../../../layout/FormLayout";
import {
  ButtonContainer,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../../components/Buttons/Buttons";
import { DelayedSpinner } from "../../../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { endpoints } from "../../../../../endpoints";
import type { OptionType, User } from "../../../../../types/types";
import type {
  AttributeSchema,
  ListItemSchema,
  ReferenceEntityColumn,
} from "../../../../../types/types.PIM";
import {
  convertUserToOption,
  useFormWrapper,
  useStoreState,
} from "../../../../../util/util";
import { Controller } from "react-hook-form";
import { SearchSelectInfiniteScroll } from "../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { zodOptionalSelectBoxDefault } from "../../../../../util/zod.util";

type FormValues = Record<string, OptionType<string> | null>;

export function AddReferenceModal({
  title,
  show,
  closeModal,
  referenceEntityId,
  columns = [],
  onSuccess,
}: {
  title: string;
  show: boolean;
  closeModal: () => void;
  referenceEntityId: string;
  columns?: ReferenceEntityColumn[];
  onSuccess: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { tenant_id, storefront_id } = useStoreState();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sortedColumns = useMemo(
    () => [...columns].sort((a, b) => a.position - b.position),
    [columns]
  );

  const attributeIdsKey = sortedColumns
    .map((col) => col.attribute_id)
    .join(",");

  const {
    data: attributes,
    error: attributesError,
    isValidating,
  } = useSWR<AttributeSchema[]>(
    tenant_id && attributeIdsKey
      ? `add-reference-attributes/${tenant_id}/${attributeIdsKey}`
      : null,
    async () => {
      const responses = await Promise.all(
        attributeIdsKey
          .split(",")
          .map((attributeId) =>
            axios.get<AttributeSchema>(
              endpoints.v2_tenants_id_pim_attributes_id(tenant_id, attributeId)
            )
          )
      );
      return responses.map((response) => response.data);
    },
    { revalidateOnFocus: false }
  );

  const formColumns = useMemo(() => {
    if (!attributes) return [];
    const attributesById = new Map(
      attributes.map((attribute) => [attribute.id, attribute])
    );
    return sortedColumns
      .map((column) => attributesById.get(column.attribute_id))
      .filter((attribute): attribute is AttributeSchema => Boolean(attribute));
  }, [attributes, sortedColumns]);

  const defaultValues = useMemo(
    () =>
      formColumns.reduce<FormValues>((acc, col) => {
        acc[col.id] = null;
        return acc;
      }, {}),
    [formColumns]
  );

  const validationSchema = useMemo(
    () =>
      z.object(
        formColumns.reduce<Record<string, z.ZodTypeAny>>((acc, col) => {
          acc[col.id] = zodOptionalSelectBoxDefault.nullable();
          return acc;
        }, {})
      ),
    [formColumns]
  );

  const methodsOfUseForm = useFormWrapper<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues,
  });

  const { handleSubmit, control, errors, formState, reset } = methodsOfUseForm;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!show) {
      reset();
    }
  }, [show, reset]);

  const onSubmit = async (values: FormValues) => {
    const apiValues = Object.entries(values).reduce<
      { column_id: string; value_id: string }[]
    >((acc, [attributeId, option]) => {
      if (option?.value) {
        const column = sortedColumns.find(
          (col) => col.attribute_id === attributeId
        );
        if (column) {
          acc.push({ column_id: column.id, value_id: option.value });
        }
      }
      return acc;
    }, []);

    if (apiValues.length === 0) {
      notifyError(t("Please enter at least one value."));
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(
        endpoints.v2_storefronts_id_reference_entities_id_data(
          storefront_id,
          referenceEntityId
        ),
        { cells: apiValues }
      );
      notifySuccess(t("Item added successfully"));
      onSuccess();
      closeModal();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        notifyError(
          (error as AxiosError).response?.data.message ??
            t("Failed to add reference")
        );
      } else {
        notifyError(t("Failed to add reference"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoadingAttributes = isValidating && !attributes;

  return (
    <SlideOut show={show} closeFlyout={closeModal} title={title}>
      {isLoadingAttributes && <DelayedSpinner />}
      {attributesError && !attributes && (
        <ErrorPlaceholder
          message={t("There was an error loading the form fields.")}
        />
      )}
      {attributes && (
        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
          {formColumns.map((col) => (
            <>
              <Controller
                control={control}
                key={col.id}
                name={col.id}
                rules={{
                  required: true,
                }}
                render={(props) =>
                  col.input_type === "user" ? (
                    <SearchSelectInfiniteScroll
                      {...props}
                      placeholder={col.display_name}
                      baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
                        storefront_id,
                        tenant_id
                      )}
                      params={(() => {
                        const params = new URLSearchParams();
                        params.append("order_by", "asc");
                        return params;
                      })()}
                      getOptions={(response: User[]) =>
                        response.reduce(
                          (acc, user) =>
                            user.is_active
                              ? [...acc, convertUserToOption(user)]
                              : acc,
                          [] as OptionType<string>[]
                        )
                      }
                      errors={errors}
                      formState={formState}
                      isClearable
                      canCreateOptions={false}
                    />
                  ) : (
                    <SearchSelectInfiniteScroll
                      key={col.id}
                      {...props}
                      placeholder={col.display_name}
                      baseUrl={endpoints.v2_tenants_id_or_slug_pim_lists_id_choices(
                        tenant_id,
                        col.list_id
                      )}
                      params={(() => {
                        const params = new URLSearchParams();
                        params.append("order_by", "asc");
                        return params;
                      })()}
                      getOptions={(response: ListItemSchema[]) =>
                        response.reduce(
                          (acc, item) => [
                            ...acc,
                            { label: item.name, value: item.id },
                          ],
                          [] as OptionType<string>[]
                        )
                      }
                      errors={errors}
                      formState={formState}
                      isClearable
                      canCreateOptions={false}
                    />
                  )
                }
              />
            </>
          ))}
          <ButtonContainer style={{ marginTop: "30px" }}>
            <TertiaryButtonLargeV2
              type="button"
              onClick={closeModal}
              fullWidth
              disabled={isSubmitting}
            >
              {t("Cancel")}
            </TertiaryButtonLargeV2>
            <PrimaryButtonLargeV2
              type="submit"
              fullWidth
              disabled={isSubmitting || formColumns.length === 0}
              loading={isSubmitting}
            >
              {t("Add")}
            </PrimaryButtonLargeV2>
          </ButtonContainer>
        </Form>
      )}
    </SlideOut>
  );
}
